/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useContext } from 'react';
import { Redirect } from 'react-router-dom';
import { useWebSocketFetcher } from 'hooks/useWebSocketFetcher';
import { iFollowupItem } from 'interfaces/iFollowupItem';
import { Modal } from '@mui/material';
import { GlobalContext } from 'providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import CasesAPI from 'api/CasesAPI';
import CreditsAPI from 'api/CreditsAPI';
import { useSnackbar } from 'notistack';
import format from 'date-fns/format';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import ECCNForm from 'components/TradeHelper/ECCNForm';
import { countries } from 'components/Common/Countries';
import { findScreeningCountryOption } from 'utils/countriesWithCodes';
import StreamEDisplay from 'components/TradeHelper/StreamEDisplay';
import { ECCNcheck } from 'api/PyAPIConnect';
import ModuleHeaderLayout from 'components/Common/Layouts/ModuleHeaderLayout';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';

const apiUrl = process.env.REACT_APP_PYAPI_URL as string;

const ECCNClassification: React.FC = (children: any) => {
  const { primary, destination, referenceHts } = children.location?.state || {};
  const destinationIso2 = findScreeningCountryOption(destination)?.iso2;
  const initialCountry = countries.find(
    (c) => c.code === destinationIso2,
  )?.label;
  const { cur_user } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const logAppAction = useLogAppAction();

  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const { streamData, isStreaming, connectWebSocket } =
    useWebSocketFetcher(apiUrl);

  const [userProductDesc, setUserProductDesc] = useState<string>('');
  const [userECCN, setUserECCN] = useState<any>([]);
  const [country, setCountry] = useState<string>('');
  const [followupItems, setFollowupItems] = useState<iFollowupItem[]>([]);
  const [clfId, setClfId] = useState<string | null>(null);
  const [cmdtDesc, setCmdtDesc] = useState<string | null>(null);
  const [HTSCode, setHTSCode] = useState<string | null>(null);
  const [explanatoryNotes, setExplanatoryNotes] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<number>(0);
  const [currentEStage, setCurrentEStage] = useState<number>(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savingTicket, setSavingTicket] = useState(false);
  const [ticketId, setTicketID] = useState('');
  const [owner, setOwner] = useState('');
  const [userHasClassify, setUserHasClassify] = useState(true);
  const [newTicketName, setNewTicketName] = useState<string>('');
  const [shareEmail, setShareEmail] = useState<string[]>([]);

  const requiredTiers = ['pro', 'business', 'enterprise'];
  const isInteractionDisabled = !userHasClassify;
  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;

  const checkForClassify = () => {
    if (
      !requiredTiers.includes(cur_user.license) &&
      cur_user?.bite_super_admin !== true
    ) {
      setUserHasClassify(false);
    }
  };

  useEffect(() => {
    checkForClassify();
    setOwner(cur_user.basics?.email || '');
  }, []);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  // Handle default stages
  useEffect(() => {
    if (followupItems.length > 0) {
      setCurrentStage(2);
    }
    if (cmdtDesc && HTSCode && explanatoryNotes) {
      setCurrentStage(3);
    }
  }, [followupItems, cmdtDesc, HTSCode]);

  const submitClassification = async (
    product_desc: string,
    followups: iFollowupItem[],
  ) => {
    if (isInteractionDisabled) {
      return;
    }
    const clf = {
      clf_id: clfId,
      product_desc: product_desc,
      followup_items: followups,
    };
    connectWebSocket('/ws/classify', clf);
  };

  const handleESubmit = async (inputValue: string, countr: string) => {
    if (isInteractionDisabled) {
      return;
    }
    if (inputValue !== '' && countr !== '') {
      logAppAction('eccn_classification:run');
      setHTSCode(inputValue);
      setCountry(countr);
      setCurrentEStage(1);
      setIsLoading(true);
      setUserECCN([]); // reset list before streaming
      try {
        const new_case = {
          eccn_full: inputValue,
          country_raw: countr,
        };

        await ECCNcheck(new_case, (chunk) => {
          setIsLoading(false);

          // smart append: remove the placeholder when breakdown arrives or when we get an explicit dismiss
          setUserECCN((prev: any[]) => {
            // 1) explicit dismiss from backend: {'section':'breakdown_status','result':{'dismiss':true}}
            if (
              chunk?.section === 'breakdown_status' &&
              chunk?.result?.dismiss
            ) {
              return prev.filter((it) => it.section !== 'breakdown_status');
            }

            // 2) when the real breakdown lands, drop any existing placeholder first
            if (chunk?.section === 'control_breakdown') {
              const withoutPlaceholder = prev.filter(
                (it) => it.section !== 'breakdown_status',
              );
              return [...withoutPlaceholder, chunk];
            }

            // 3) otherwise, regular append (but avoid duplicate placeholders)
            if (chunk?.section === 'breakdown_status') {
              const alreadyHasPlaceholder = prev.some(
                (it) => it.section === 'breakdown_status',
              );
              if (alreadyHasPlaceholder) {
                return prev; // don't add a second placeholder
              }
            }

            return [...prev, chunk];
          });
        });

        enqueueSnackbar('Successfully received controls', {
          variant: 'success',
        });
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        const error_msg =
          'Error receiving controls, please try again and contact the BITE Team if the problem persist';
        enqueueSnackbar(error_msg, { variant: 'error' });
      }
    } else {
      setIsLoading(false);
      enqueueSnackbar(
        'Please input the ECCN and Export Country to receive results.',
        { variant: 'error' },
      );
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const handleTicket = async (values: any) => {
    if (isInteractionDisabled) {
      return;
    }
    const new_case = {
      groupId: cur_user.selected_team.id,
      ...values.basics,
      ...values,
    };
    const resp = await CasesAPI.createCase({ newCase: new_case });

    if (resp?.success) {
      setTicketID(resp.result._id);
      enqueueSnackbar('Successfully saved work', { variant: 'success' });
      shareEmail.forEach((email) => {
        // Call to notification sending function here
        handleShareTicketNotification(
          values.ticketName,
          cur_user,
          email,
          resp.result._id,
        );
      });
      setSavingTicket(false);
      setModalOpen(false);
      setNewTicketName('');
    } else if (resp) {
      const error_msg =
        'Error saving, please try again and contact the BITE Team if the problem persist';
      enqueueSnackbar(error_msg, { variant: 'error' });
      setSavingTicket(false);
      setModalOpen(false);
    }
  };

  const saveTicket = async (name: string) => {
    if (isInteractionDisabled) {
      return;
    }
    logAppAction('eccn_classification:save');
    setSavingTicket(true);
    const ownerQuery = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };

    const query: any = {
      owner: ownerQuery,
      ticketName: name,
      lastUpdate: formatted_dt,
      docType: 'ECCN Check',
      sharedWith: shareEmail,
      activeFilter: '',
      selectedOpts: [],
      status: 'Complete',
      favorite: false,
      groupName: cur_user.selected_team.name,
      classificationValue: userProductDesc,
      eccnResults: userECCN,
      commodity: {
        commodityName: '',
        eccn: primary ? primary : HTSCode,
        destinationCountry: country,
      },
      activity: {
        user: {
          kc_id: cur_user.basics?.id,
          email: cur_user.basics?.email,
          firstName: cur_user.basics?.firstName,
          lastName: cur_user.basics?.lastName,
        },
        action: 'saved classification',
        timeStamp: Date.now(),
      },
    };
    handleTicket(query);
  };

  const formatExplanatoryNote = (rawNotes: string) => {
    const cleanedNotes = rawNotes
      .replace(/[\[\]']/g, '')
      .replace(/,(\s)?$/gm, '')
      .replace(/^°/gm, '');

    const lines = cleanedNotes
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const result: JSX.Element[] = [];
    let bulletItems: string[] = [];

    const flushBullets = () => {
      if (bulletItems.length > 0) {
        result.push(
          <ul
            key={`ul-${result.length}`}
            style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}
          >
            {bulletItems.map((item, i) => (
              <li
                key={i}
                style={{ marginBottom: '0.25rem' }}
                dangerouslySetInnerHTML={{
                  __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                }}
              />
            ))}
          </ul>,
        );
        bulletItems = [];
      }
    };

    lines.forEach((line, index) => {
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      const isHeading =
        /^\*\*/.test(line) || (/^[A-Z\s]+$/.test(line) && line.length < 80);
      const isBullet = /^[-•*]|\(\w\)|^\(\d+\)|^\d+\)/.test(line);

      if (isHeading) {
        flushBullets();
        result.push(
          <h2
            key={`h-${index}`}
            style={{ margin: '0.5rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      } else if (isBullet) {
        bulletItems.push(formatted);
      } else {
        flushBullets();
        result.push(
          <p
            key={`p-${index}`}
            style={{ margin: '0.25rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      }
    });

    flushBullets();

    return result;
  };

  // define preferred order
  const preferredOrder = [
    'Summary of Applicable Controls',
    'Control Check Outcome',
  ];

  /* -------------------- Help content -------------------- */
  const helpContent = (
    <p className='module-header-tooltip-text'>
      Check for tariffs that may apply to your products and receive the total
      calculation that you will need to account for in a transaction.
    </p>
  );
  return (
    <>
      <ModuleHeaderLayout title='ECCN Check' helpContent={helpContent}>
        <RequiredLicense userLicense={normalizedLicense} minimumLicense='pro' />
        <div
          className='bill-row'
          style={{
            position: 'relative',
            width: '100%',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            paddingTop: '4px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              marginBottom: '2rem',
            }}
          >
            <div style={{ width: '100%' }}>
              <ECCNForm
                onSubmit={handleESubmit}
                isStreaming={isLoading}
                initialValue={primary ? primary : ''}
                initialCountry={initialCountry}
                referenceHts={referenceHts}
                userHasSearch={userHasClassify}
                hasLicense={Boolean(
                  userECCN.find(
                    (item: { section: string }) =>
                      item.section === 'license_requirement_note',
                  ),
                )}
                licenseMsg={
                  userECCN.find(
                    (item: { section: string }) =>
                      item.section === 'license_requirement_note',
                  ) || []
                }
                hasFinished={Boolean(
                  userECCN.find(
                    (item: { section: string }) =>
                      item.section === 'control_outcome',
                  ),
                )}
                setModalOpen={setModalOpen}
                isInteractionDisabled={isInteractionDisabled}
              />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              borderTop: '2px solid var(--bg-secondary)',
            }}
          >
            <div
              style={{
                width: '100%',
                marginBottom: '20px',
                paddingTop: '2rem',
              }}
            >
              <div>
                <StreamEDisplay
                  message={[...userECCN].sort((a, b) => {
                    const aIndex = preferredOrder.indexOf(a.result?.heading);
                    const bIndex = preferredOrder.indexOf(b.result?.heading);

                    // If both are in preferredOrder, compare their indexes
                    if (aIndex !== -1 && bIndex !== -1) {
                      return aIndex - bIndex;
                    }

                    // If only one is in preferredOrder, that one comes first
                    if (aIndex !== -1) {
                      return -1;
                    }
                    if (bIndex !== -1) {
                      return 1;
                    }

                    // Otherwise, keep their original order
                    return 0;
                  })}
                  ticket={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
        <Modal open={isModalOpen} onClose={handleCloseModal}>
          <CreateTicketWizard
            cur_user={cur_user}
            handleSaveTicket={saveTicket}
            newTicketName={newTicketName}
            setNewTicketName={setNewTicketName}
            shareEmail={shareEmail}
            setShareEmail={setShareEmail}
            savingTicket={savingTicket}
          />
        </Modal>
      </ModuleHeaderLayout>
    </>
  );
};

export default ECCNClassification;
